/* eslint-disable indent */
import './index.scss'

import React, { useState, useRef } from 'react'
import { get } from 'lodash'
import T from 'prop-types'
import BEMHelper from 'react-bem-helper'

import { Icon } from '@components'
// import ColorSection from '../ColorSection'
import Video from '../Video'
import Toggle from '../Toggle'
import Overview from '../Overview'
import Button from '../Button'
import useGestures from '../../hooks/useGestures'
import getColor from '../../lib/colors'

const MARGIN = 3

const bem = new BEMHelper('recipe-preview')

function Step({
  video,
  ingredients,
  title,
  active,
  persons,
  nextStep,
  portions,
  type,
}) {
  const stepRef = useRef()
  const ingredientsRef = useRef()
  const [expanded, setExpanded] = useState(false)
  const [height, setHeight] = useState(0)

  const toggle = () => {
    setExpanded(expanded => !expanded)

    if (type === 'intro' && stepRef.current) {
      setHeight(stepRef.current?.offsetHeight)
    } else {
      setHeight(ingredientsRef.current?.offsetHeight || 0)
    }
  }

  if (type === 'intro') {
    return (
      <li {...bem('step', { active })} ref={stepRef}>
        <div {...bem('step-content')}>
          <p {...bem('instructions', 'single')}>{title}</p>
        </div>

        <Toggle
          {...bem('portions')}
          value={persons}
          options={portions.options}
          type="small"
          onChange={value => {
            portions.setPersons(value)
            nextStep()
          }}
          unitType="personer"
        />

        {ingredients?.length > 0 && (
          <div {...bem('step-content')}>
            <div
              {...bem('ingredients', { expanded, large: true })}
              ref={ingredientsRef}
              style={{
                '--move': `-${height / 2 - 20}px`,
                minHeight: expanded ? height : 0,
              }}
            >
              <button
                type="button"
                {...bem('ingredients-toggle')}
                onClick={toggle}
              >
                Alle ingredienser
                <Icon
                  icon="chevron"
                  direction="down"
                  size="small"
                  {...bem('chevron')}
                />
              </button>
              <ul {...bem('ingredient-list', 'large')}>
                {ingredients.map(({ id, quantity, title } = {}) => (
                  <li {...bem('ingredient')} key={id}>
                    {quantity && quantity[persons] && `${quantity[persons]} `}
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </li>
    )
  }

  return (
    <li {...bem('step', { active })}>
      {video && (
        <Video
          url={video.asset.url}
          thumbnailUrl={`${video.asset.thumbnail}?time=0`}
          active={active}
          moveProgress={expanded ? height / 2 - 20 : 0}
          {...bem('video')}
        />
      )}
      <div {...bem('step-content')}>
        {ingredients?.length > 0 && (
          <div
            {...bem('ingredients', { expanded })}
            ref={ingredientsRef}
            style={{ '--move': `-${height / 2 - 20}px` }}
          >
            <button
              type="button"
              {...bem('ingredients-toggle')}
              onClick={toggle}
            >
              Ingredienser
              <Icon
                icon="chevron"
                direction="down"
                size="small"
                {...bem('chevron')}
              />
            </button>
            <ul {...bem('ingredient-list')}>
              {ingredients.map(({ id, quantity, title } = {}) => (
                <li {...bem('ingredient')} key={id}>
                  {quantity && quantity[persons] && `${quantity[persons]} `}
                  {title}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p
          {...bem('instructions', { single: !video, 'move-down': expanded })}
          style={{ '--move': `${Math.max(0, height / 2 - 20)}px` }}
        >
          {title}
        </p>
      </div>
    </li>
  )
}

function Menu({ expanded, toggle, steps, ingredients, setActive, persons }) {
  const goTo = index => {
    setActive(index)
    toggle()
  }

  return (
    <>
      <div {...bem('overview', { expanded })}>
        {expanded && (
          <Overview
            steps={steps}
            ingredients={ingredients}
            persons={persons}
            goTo={goTo}
          />
        )}
      </div>
      <button type="button" {...bem('menu', { expanded })} onClick={toggle}>
        <svg viewBox="0 0 28 28" {...bem('menu-icon')}>
          <polyline {...bem('icon-line')} points="0,11 28,11" />
          <polyline {...bem('icon-line')} points="0,19 28,19" />
        </svg>
      </button>
    </>
  )
}

const PORTIONS_DEFAULT = [
  ['two', 2],
  ['four', 4],
  ['six', 6],
  ['eight', 8],
]

const MOVE_AMOUNT = 40
export default function RecipePreview({
  title,
  preamble,
  recipe: { node: recipe },
  level = 2,
}) {
  const stepsRef = useRef()
  const [active, setActive] = useState(0)
  const [persons, setPersons] = useState('two')
  const [expanded, setExpanded] = useState(false)
  const [move, setMove] = useState(null)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useGestures(stepsRef, {
    touchMove: ({ positions: { diffX } }) => {
      setMove(-diffX)
    },
    touchEnd: ({ positions: { diffX } }) => {
      setMove(null)

      if (diffX > MOVE_AMOUNT) {
        nextStep()
      } else if (diffX < -MOVE_AMOUNT) {
        prevStep()
      }
    },
  })

  const toggleMenu = () => {
    setExpanded(expanded => !expanded)
  }

  const ingredients = [].concat(
    [],
    ...(recipe.steps || []).map(({ ingredients }) =>
      (ingredients || []).filter(
        ({ showInIngredientList }) => showInIngredientList !== false,
      ),
    ),
  )

  const portions =
    recipe?.quantitySettings?.mode === 'full'
      ? PORTIONS_DEFAULT
      : [
          ['two', recipe?.quantitySettings?.singleHeading || 'enkel'],
          ['four', recipe?.quantitySettings?.doubleHeading || 'dobbel'],
        ]

  const steps = [
    {
      id: 'intro',
      title:
        recipe?.quantitySettings?.mode === 'full'
          ? 'Hvor mange skal spise?'
          : 'Hvor mye vil du lage?',
      type: 'intro',
      ingredients,
      portions: { options: portions, setPersons },
    },
    ...(recipe.steps || []),
    {
      title: 'Vel bekomme!',
      id: 'final',
    },
  ]
  const count = steps.length

  const nextStep = () => {
    setActive(active => Math.min(count - 1, active + 1))
    setExpanded(false)
  }

  const prevStep = () => {
    setActive(active => Math.max(0, active - 1))
    setExpanded(false)
  }

  const colors = getColor(
    get(recipe, 'image.asset.metadata.palette'),
    get(recipe, 'color'),
  )

  const percentage = -(100 / count) * active
  const progress = (100 / count) * (active + 1)

  const Title = level === 1 ? 'h1' : 'h2'

  return (
    <section
      {...bem('')}
      style={{
        '--margin': `${MARGIN}px`,
        '--progress': `${progress}%`,
      }}
    >
      <header {...bem('intro')}>
        <Title {...bem('title', `level-${level || 2}`)}>
          {title || recipe.title}
        </Title>
        <p {...bem('preamble', `level-${level || 2}`)}>
          {preamble || recipe.preamble}
        </p>
        <Button
          {...bem('action')}
          label="Last ned appen"
          to="/last-ned"
          primary
        />
      </header>

      <div
        {...bem('content')}
        style={{
          '--color-base': colors.base,
          '--color-highlighted': colors.highlighted,
          '--color-muted': colors.muted,
        }}
      >
        <div {...bem('wrapper')}>
          <div {...bem('mobile')}>
            <svg viewBox="0 0 90 195" {...bem('aspect')} />

            <div
              {...bem('progress', { hidden: expanded })}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div {...bem('bar')}>
                <div {...bem('count', { end: progress > 98 })}>
                  {active + 1}
                </div>
              </div>
            </div>
            <ol
              {...bem('steps', { move: move !== null })}
              ref={stepsRef}
              style={{
                width: `calc(${count * 100}% + ${MARGIN * count}px)`,
                transform:
                  move !== null
                    ? `translateX(calc(${percentage}% + ${move}px))`
                    : `translateX(${percentage}%)`,
              }}
            >
              {steps.map((step, index) => (
                <Step
                  key={step.id}
                  active={index === active}
                  {...step}
                  persons={persons}
                  nextStep={nextStep}
                />
              ))}
            </ol>

            <Menu
              expanded={expanded}
              toggle={toggleMenu}
              steps={steps}
              setActive={setActive}
              ingredients={ingredients}
              persons={persons}
            />
          </div>
          <button
            type="button"
            aria-label="Forrige steg"
            {...bem('control', 'left')}
            onClick={prevStep}
          >
            <Icon icon="chevron" direction="left" />
          </button>
          <button
            type="button"
            aria-label="Neste steg"
            {...bem('control', 'right')}
            onClick={nextStep}
          >
            <Icon icon="chevron" />
          </button>
        </div>

        <p {...bem('caption')}>
          Få denne og over 1000 andre oppskrifter i Foodsteps-appen.
        </p>
      </div>
    </section>
  )
}

RecipePreview.propTypes = {
  title: T.string,
  preamble: T.string,
  recipe: T.object.isRequired,
  level: T.oneOf([1, 2]),
}

Menu.propTypes = {
  persons: T.string.isRequired,
  expanded: T.bool,
  toggle: T.func.isRequired,
  steps: T.array.isRequired,
  ingredients: T.array.isRequired,
  setActive: T.func.isRequired,
}

Step.propTypes = {
  video: T.object,
  ingredients: T.array,
  title: T.string.isRequired,
  persons: T.string.isRequired,
  active: T.bool,
  type: T.string,
  nextStep: T.func.isRequired,
  portions: T.object,
}
